import axios from "axios";
import configuration from "../configuration/configuration";

/**
 * Paiement carte sur le lecteur Zettle du comptoir (routes /api/invoices/{id}/terminal, /api/terminal et
 * /api/appareil/etat de l'API). Le site met la demande en file, le téléphone du comptoir la lance sur le lecteur.
 * Les erreurs sont renvoyées telles quelles : le message du serveur est dans error.response.data.message.
 */
export default class TerminalAPI {

    async request(method, url, data) {
        const response = await axios({
            method: method,
            url: `${configuration.API}/api/${url}`,
            headers: {'content-type': 'application/json'},
            data: data
        });

        return response.data;
    }

    /** montant en euros (nombre) */
    envoyer(factureId, montant) {
        return this.request('post', `invoices/${factureId}/terminal`, {montant: montant});
    }

    statut(id) {
        return this.request('get', `terminal/${id}`);
    }

    annuler(id) {
        return this.request('post', `terminal/${id}/annuler`);
    }

    etatAppareil() {
        return this.request('get', 'appareil/etat');
    }
}

/** Statuts définitifs d'un paiement : plus rien à attendre du lecteur. */
export const STATUTS_FINAUX = ["completed", "cancelled", "failed", "mismatch"];

/** "1 240.50" (format de l'API), "12,5" ou 12.5 -> 124050, 1250... ; null si ce n'est pas un montant. */
export function versCentimes(valeur) {
    if (valeur === null || valeur === undefined) {
        return null;
    }
    const texte = String(valeur).replace(/[\s  ]/g, "").replace(",", ".");
    if (!/^\d+(\.\d{0,2})?$/.test(texte)) {
        return null;
    }

    return Math.round(parseFloat(texte) * 100);
}

/** 124050 -> "1 240,50 €" */
export function formatEuros(centimes) {
    const euros = Math.floor(centimes / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    return euros + "," + String(centimes % 100).padStart(2, "0") + " €";
}

/** "2026-10-16T14:05:00+02:00" -> "14:05" (heure locale du navigateur) */
export function heureMinute(iso) {
    const date = new Date(iso);

    return String(date.getHours()).padStart(2, "0") + ":" + String(date.getMinutes()).padStart(2, "0");
}

/**
 * Voyant du téléphone du comptoir dans l'en-tête du tableau de bord : texte court, et détail en info-bulle ;
 * null si tout va bien ou si aucun téléphone ne s'est jamais connecté (boutique sans lecteur).
 */
export function bandeauComptoir(etat) {
    if (!etat || !etat.dernier_contact) {
        return null;
    }
    const contact = "Dernier contact du téléphone : " + heureMinute(etat.dernier_contact);
    if (!etat.en_ligne) {
        return {
            couleur: "danger",
            court: "Téléphone hors ligne depuis " + heureMinute(etat.dernier_contact),
            message: "Le téléphone du comptoir ne répond plus depuis " + heureMinute(etat.dernier_contact)
                + " : SMS et paiement carte indisponibles",
        };
    }
    if (!etat.premier_plan) {
        return {
            couleur: "warning",
            court: "Appli du téléphone fermée",
            message: "L'appli Akitatek n'est pas ouverte sur le téléphone : paiement carte indisponible. " + contact,
        };
    }
    if (etat.zettle_connecte === false) {
        return {
            couleur: "warning",
            court: "Téléphone non connecté à Zettle",
            message: "Le téléphone du comptoir n'est pas connecté à Zettle : paiement carte indisponible. " + contact,
        };
    }

    return null;
}

/** Texte du résultat final d'un paiement, affiché dans la fenêtre d'attente. */
export function messageResultat(paiement) {
    switch (paiement.statut) {
        case "completed":
            return "Paiement accepté : " + formatEuros(paiement.montant_paye_centimes ?? paiement.montant_centimes)
                + (paiement.carte_marque ? " — " + paiement.carte_marque : "")
                + (paiement.carte_masquee ? " " + paiement.carte_masquee : "");
        case "cancelled":
            return "Paiement annulé" + (paiement.erreur ? " : " + paiement.erreur : ".");
        case "failed":
            return "Paiement refusé ou en échec" + (paiement.erreur ? " : " + paiement.erreur : ".");
        case "mismatch":
            return "À vérifier dans Zettle : " + (paiement.erreur || "le montant payé diffère du montant demandé.") + " Aucun règlement enregistré.";
        case "taken":
            return "Présentez la carte sur le lecteur… (si le téléphone affiche un résultat, appuyez sur OK)";
        default:
            return "Envoi au téléphone du comptoir…";
    }
}
